import { createColumnHelper, flexRender, useTable } from "@tanstack/react-table"
import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { cn } from "design-system/utils"
import config from "lib/config"
import { features, type Features } from "lib/table"
import { ExternalLink, Trash } from "lucide-react"
import { MessengerIcon } from "modules/shared/icons/messenger"
import { useMessengerIntegrationsQuery } from "modules/settings/hooks/messenger/useMessengerIntegrationsQuery"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { MessengerIntegrationsResponseData } from "schema"
import { z } from "zod"
import { DeleteMessengerIntegration } from "./DeleteMessengerIntegration"

const columnHelper = createColumnHelper<
  Features,
  z.infer<typeof MessengerIntegrationsResponseData>[number]
>()

export const MessengerIntegrationsTable = () => {
  const { t } = useTranslation()
  const { data, isPending } = useMessengerIntegrationsQuery()
  const [integrationToDelete, setIntegrationToDelete] = useState<
    z.infer<typeof MessengerIntegrationsResponseData>[number] | null
  >(null)
  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor("name", {
          header: t("settings.integrations.messenger.table.name"),
          meta: { colSpan: 5 },
          cell: (props) => {
            const { profilePicture, pageId } = props.row.original

            return (
              <div className="flex gap-3 items-center">
                {profilePicture ? (
                  <img
                    src={profilePicture}
                    className="object-contain rounded-full"
                    width={36}
                    height={36}
                  />
                ) : (
                  <MessengerIcon width={36} height={36} />
                )}
                <div className="flex flex-col">
                  <span className="font-medium">{props.getValue()}</span>
                  <span className="text-xs text-muted-foreground">{pageId}</span>
                </div>
              </div>
            )
          },
        }),
        columnHelper.accessor("failedAt", {
          header: t("settings.integrations.messenger.table.status"),
          meta: { colSpan: 2 },
          cell: (props) => {
            if (!props.getValue()) {
              return (
                <Badge variant="success" size="sm">
                  {t("settings.integrations.messenger.table.statusConnected")}
                </Badge>
              )
            }

            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="negative" size="sm">
                    {t("settings.integrations.messenger.table.statusFailed")}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  {t("settings.integrations.messenger.table.failedTooltip")}
                </TooltipContent>
              </Tooltip>
            )
          },
        }),
        columnHelper.accessor("campaignsCount", {
          header: t("settings.integrations.messenger.table.campaigns"),
          meta: { colSpan: 1 },
        }),
        columnHelper.accessor("createdAt", {
          header: t("settings.integrations.messenger.table.connectedAt"),
          cell: (props) => <DateDisplay date={props.getValue()} />,
          meta: { colSpan: 2 },
        }),
        columnHelper.display({
          header: "",
          id: "actions",
          meta: { align: "right" },
          cell: (props) => {
            return (
              <div className="flex justify-end gap-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button type="button" size={"icon"} variant={"ghost"} asChild>
                      <a
                        href={`https://www.facebook.com/${props.row.original.pageId}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <ExternalLink />
                      </a>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t("settings.integrations.messenger.table.viewOnFacebook")}
                  </TooltipContent>
                </Tooltip>
                <Button
                  type="button"
                  size={"icon"}
                  variant={"ghost"}
                  onClick={() => setIntegrationToDelete(props.row.original)}
                >
                  <Trash />
                </Button>
              </div>
            )
          },
        }),
      ]),
    [t],
  )

  const table = useTable({
    data: data ?? [],
    columns,
    features,
  })

  function renderContent() {
    if (isPending || data === undefined) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (data.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("settings.integrations.messenger.table.noIntegrations")}
          cta={{
            label: t("settings.integrations.messenger.table.addIntegration"),
            href: config.messengerLoginUrl,
          }}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow key={row.original.id}>
        {row.getVisibleCells().map((cell) => (
          <TableCell
            key={cell.id}
            className={cn(cell.column.columnDef.meta?.className)}
            align={cell.column.columnDef.meta?.align}
            {...cell.column.columnDef.meta}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  return (
    <>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} {...header.column.columnDef.meta}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody className="**:data-[slot=table-cell]:first:w-8">{renderContent()}</TableBody>
      </Table>
      <DeleteMessengerIntegration
        integration={integrationToDelete}
        onOpenChange={(isOpen) => {
          if (!isOpen) setIntegrationToDelete(null)
        }}
      />
    </>
  )
}
